// The user editor (design/kit/components/SettingsEditor, Dialog; the Settings board's Users editor):
// Add user opens the dialog, a row opens the slide-over editor.
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import type { Api, HistoryLine, SignedInUser, UserChanges, UserRow } from '../auth/api';
import { ErrorLine } from '../auth/SignInLayout';
import { roleLabels, type RoleCode } from '../roles';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { useModalLayer } from '../shell/modal-layer';
import { useOnline } from '../shell/online';
import { CheckIcon, XIcon } from '../shell/icons';
import { stamp } from './code-rule-lines';
import { HistoryRows } from './HistoryRows';
import {
  looksLikeEmail,
  managesRole,
  roleCards,
  statusClass,
  statusLabel,
  usersCopy as c,
  validInitials,
} from './user-lines';

type Draft = {
  name: string;
  email: string;
  initials: string;
  role: RoleCode | '';
  outToday: boolean;
};

const draftOf = (user: UserRow | null): Draft => ({
  name: user?.name ?? '',
  email: user?.email ?? '',
  initials: user?.initials ?? '',
  role: user?.role ?? '',
  outToday: user?.status === 'out_today',
});

const sameDraft = (a: Draft, b: Draft) =>
  a.name === b.name &&
  a.email === b.email &&
  a.initials.toUpperCase() === b.initials.toUpperCase() &&
  a.role === b.role &&
  a.outToday === b.outToday;

type UserEditorProps = {
  api: Api;
  actor: SignedInUser;
  // null: Add user.
  user: UserRow | null;
  onClose: () => void;
  // After a save or a status change: reload the list and show this toast.
  onDone: (toast: string) => void;
};

type Confirm = 'deactivate' | 'discard' | null;

export function UserEditor({ api, actor, user, onClose, onDone }: UserEditorProps) {
  const isNew = user === null;
  const self = !isNew && user.id === actor.id;
  const [orig] = useState(() => draftOf(user));
  const [d, setD] = useState(orig);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailTaken, setEmailTaken] = useState(false);
  const [signMsg, setSignMsg] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [history, setHistory] = useState<HistoryLine[] | null>(null);
  const [histOpen, setHistOpen] = useState(false);
  const userId = user?.id;

  // History: the changes to this person, read once when the editor opens.
  useEffect(() => {
    if (!userId) return;
    let live = true;
    api.userHistory(userId).then((res) => {
      if (live && res.ok) setHistory(res.body.history);
    });
    return () => {
      live = false;
    };
  }, [api, userId]);

  const set = (p: Partial<Draft>) => {
    setD((prev) => ({ ...prev, ...p }));
    if (p.email !== undefined) setEmailTaken(false);
  };

  const dirty = !sameDraft(orig, d);
  const emailOk = looksLikeEmail(d.email.trim());
  const valid =
    d.name.trim() !== '' && emailOk && d.role !== '' && validInitials(d.initials) && !emailTaken;
  const layerRef = useRef<HTMLElement>(null);
  useModalLayer(layerRef);
  const online = useOnline();
  // Offline, nothing can save (spec C7).
  const canSave = dirty && valid && !busy && online;
  const shownName = isNew ? 'the new user' : user.name;

  // Leaving with unsaved changes asks first (SettingsEditor card).
  const requestClose = useCallback(() => {
    if (dirty) setConfirm('discard');
    else onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose]);

  const fail = (err: string) => {
    if (err === 'email_taken') setEmailTaken(true);
    else setError(c.somethingWrong);
  };

  async function save() {
    if (!canSave || d.role === '') return;
    setBusy(true);
    setError(null);
    if (isNew) {
      const res = await api.addUser({
        email: d.email.trim(),
        name: d.name.trim(),
        initials: d.initials.trim().toUpperCase(),
        role: d.role,
      });
      setBusy(false);
      if (!res.ok) return fail(res.error);
      return onDone(c.invitationSent(res.body.user.email));
    }
    const changes: UserChanges = {};
    if (d.name.trim() !== orig.name) changes.name = d.name.trim();
    if (d.initials.trim().toUpperCase() !== orig.initials) changes.initials = d.initials;
    if (d.role !== orig.role) changes.role = d.role;
    if (d.outToday !== orig.outToday) changes.outToday = d.outToday;
    const res = await api.updateUser(user.id, changes);
    setBusy(false);
    if (!res.ok) return fail(res.error);
    onDone(c.saved(res.body.user.name));
  }

  async function setStatus(status: 'active' | 'inactive') {
    if (isNew) return;
    setConfirm(null);
    setBusy(true);
    const res = await api.updateUser(user.id, { status });
    setBusy(false);
    if (!res.ok) return fail(res.error);
    onDone(status === 'inactive' ? c.deactivated(user.name) : c.reactivated(user.name));
  }

  async function resend() {
    if (isNew) return;
    const res = await api.resendInvitation(user.id);
    if (!res.ok) return fail(res.error);
    onDone(c.invitationSent(user.email));
  }

  async function signInAction(kind: 'reset' | 'signOut') {
    if (isNew) return;
    setSignMsg(null);
    if (kind === 'reset') {
      const res = await api.sendResetLink(user.id);
      if (!res.ok) return fail(res.error);
      setSignMsg(c.resetSent(user.email));
    } else {
      const res = await api.signOutUser(user.id);
      if (!res.ok) return fail(res.error);
      setSignMsg(c.signedOutOf(res.body.sessions));
    }
  }

  const status = user?.status;
  const roleNow = d.role || user?.role;
  const canAway =
    !isNew &&
    (status === 'active' || status === 'out_today') &&
    (roleNow === 'renewal_va' || roleNow === 'account_executive');
  const showSignIn =
    !isNew && actor.role === 'Admin' && (status === 'active' || status === 'out_today');
  const canDeactivate = !isNew && !self && status !== 'inactive' && status !== 'invited';
  const canReactivate = !isNew && status === 'inactive';
  const roleNote = self
    ? actor.role === 'Admin'
      ? c.roleNoteAdminSelf
      : c.roleNoteSelf
    : actor.role === 'Admin'
      ? null
      : c.roleNoteManager;

  const body = (
    <div className="p-body">
      {status === 'invited' && (
        <div className="amb" style={{ justifyContent: 'space-between' }}>
          <span>{c.invitedLine}</span>
          <button type="button" className="btn btn-sm" onClick={resend}>
            {c.resendInvitation}
          </button>
        </div>
      )}
      <Section id="ue-s1" title={c.account}>
        <div className="f2">
          <div>
            <label className="flab" htmlFor="ue-name">
              Name <span className="req">*</span>
            </label>
            <input
              id="ue-name"
              className="input"
              type="text"
              maxLength={100}
              value={d.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </div>
          <div>
            <label className="flab" htmlFor="ue-ini">
              Initials <span className="req">*</span>
            </label>
            <input
              id="ue-ini"
              className="input mono"
              type="text"
              maxLength={3}
              value={d.initials}
              onChange={(e) => set({ initials: e.target.value.toUpperCase() })}
            />
            <p className="fnote">{c.usedInSheet}</p>
          </div>
        </div>
        <div>
          <label className="flab" htmlFor="ue-email">
            Email <span className="req">*</span>
          </label>
          <input
            id="ue-email"
            className="input"
            type="email"
            maxLength={254}
            disabled={!isNew}
            value={d.email}
            onChange={(e) => set({ email: e.target.value })}
          />
          {isNew && d.email.trim() !== '' && !emailOk && <p className="ferr">{c.validEmail}</p>}
          {emailTaken && <p className="ferr">{c.emailTaken}</p>}
          {!isNew && <p className="fnote">{c.emailFixed}</p>}
        </div>
        <div role="radiogroup" aria-labelledby="ue-role">
          <div id="ue-role" className="flab">
            Role <span className="req">*</span>
          </div>
          <div className="rcards" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
            {roleCards.map((r) => {
              const off = !managesRole(actor.role, r.code) || (self && r.code !== d.role);
              return (
                <button
                  key={r.code}
                  type="button"
                  className={d.role === r.code ? 'rcard on' : 'rcard'}
                  role="radio"
                  aria-checked={d.role === r.code}
                  disabled={off}
                  onClick={() => set({ role: r.code })}
                >
                  <span className="rcard-t">{roleLabels[r.code]}</span>
                  <span className="rcard-s">{r.sub}</span>
                </button>
              );
            })}
          </div>
          {roleNote && <p className="fnote">{roleNote}</p>}
        </div>
      </Section>
      {canAway && (
        <Section id="ue-s5" title={c.timeAway}>
          <label className="ckline">
            <input
              type="checkbox"
              checked={d.outToday}
              onChange={(e) => set({ outToday: e.target.checked })}
            />
            {c.outToday}
          </label>
          <p className="esub">{c.outTodayNote}</p>
          <div className="row g6">
            <span className="t12 muted">{c.datedAway}</span>
            <span className="tag t-grey">{c.phase2}</span>
          </div>
        </Section>
      )}
      {showSignIn && (
        <Section id="ue-s4" title={c.signIn} tag={c.adminOnly}>
          <div className="mini">
            <div className="hrow">
              <span className="ink w5">{c.twoStep}</span>
              <span className="muted">{c.twoStepLine(user!.email)}</span>
            </div>
          </div>
          <div className="row g8 wrap">
            <button type="button" className="btn" onClick={() => signInAction('reset')}>
              {c.resetPassword}
            </button>
            {!self && (
              <button type="button" className="btn" onClick={() => signInAction('signOut')}>
                {c.signOutAll}
              </button>
            )}
          </div>
          {signMsg && (
            <span className="okline" role="status">
              <CheckIcon />
              {signMsg}
            </span>
          )}
        </Section>
      )}
      {!isNew && (
        <section className="esec" aria-label={c.history}>
          <button
            type="button"
            className="linkbtn"
            style={{ alignSelf: 'flex-start' }}
            aria-expanded={histOpen}
            onClick={() => setHistOpen(!histOpen)}
          >
            {histOpen ? c.hideHistory : history ? c.historyCount(history.length) : c.history}
          </button>
          {histOpen && history && (
            <HistoryRows
              rows={history.map((h) => ({ ...h, when: `${stamp(h.at)}, ${h.by}` }))}
              empty={c.noHistory}
            />
          )}
        </section>
      )}
      {error && <ErrorLine text={error} />}
    </div>
  );

  const footer = (
    <>
      <button type="button" className="btn" onClick={requestClose}>
        {c.cancel}
      </button>
      {canDeactivate && (
        <button
          type="button"
          className="btn btn-danger"
          disabled={!online}
          onClick={() => setConfirm('deactivate')}
        >
          {c.deactivate}
        </button>
      )}
      {canReactivate && (
        <button
          type="button"
          className="btn"
          disabled={busy || !online}
          onClick={() => setStatus('active')}
        >
          {c.reactivate}
        </button>
      )}
      <span className="grow"></span>
      {dirty && !isNew && <span className="t12 amber w5">{c.unsaved}</span>}
      <button type="button" className="btn btn-primary" disabled={!canSave} onClick={save}>
        {isNew ? c.saveAndInvite : c.save}
      </button>
    </>
  );

  const head = (
    <div className="p-head">
      <div className="row g8 wrap" style={{ minWidth: 0 }}>
        <h2 id="ue-title" className="dtitle">
          {isNew ? c.addUser : d.name.trim() || user.name}
        </h2>
        {isNew ? (
          <span className="t12 muted ed-sub">{c.newSub}</span>
        ) : (
          <span className={statusClass(user.status)}>{statusLabel(user.status)}</span>
        )}
      </div>
      <button type="button" className="xbtn" aria-label="Close" onClick={requestClose}>
        <XIcon />
      </button>
    </div>
  );

  return (
    <>
      <button
        type="button"
        className="scrim"
        aria-label="Close user editor"
        onClick={requestClose}
      />
      {isNew ? (
        <div
          ref={layerRef as RefObject<HTMLDivElement>}
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ue-title"
        >
          {head}
          {body}
          <div className="m-foot">{footer}</div>
        </div>
      ) : (
        <aside
          ref={layerRef}
          className="panel ed560"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ue-title"
        >
          {head}
          {body}
          <div className="p-foot">{footer}</div>
        </aside>
      )}
      {confirm === 'deactivate' && !isNew && (
        <ConfirmPopup
          title={c.deactivateTitle(user.name)}
          line={c.deactivateLine(user.name)}
          action={c.deactivate}
          danger
          onConfirm={() => setStatus('inactive')}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'discard' && (
        <ConfirmPopup
          title={c.discardTitle(shownName)}
          line={c.discardLine}
          action={c.discard}
          cancel={c.keepEditing}
          danger
          onConfirm={onClose}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

function Section({
  id,
  title,
  tag,
  children,
}: {
  id: string;
  title: string;
  tag?: string;
  children: ReactNode;
}) {
  return (
    <section className="esec" aria-labelledby={id}>
      {tag ? (
        <div className="esec-h">
          <h3 id={id} className="h3">
            {title}
          </h3>
          <span className="tag t-grey">{tag}</span>
        </div>
      ) : (
        <h3 id={id} className="h3">
          {title}
        </h3>
      )}
      {children}
    </section>
  );
}
